import React, { useEffect, useState, useRef } from "react";
import {
    Layout,
    Row,
    Col,
    message,
    Button,
    Spin,
    Modal,
    Typography,
    Checkbox,
    Form,
    Divider,
    DatePicker
} from "antd";
import { DownloadOutlined } from '@ant-design/icons';
import AnalyticsOverviewCard from "../components/Analytics/AnalyticsOverviewCard";
import AnalyticsTable from "../components/Analytics/AnalyticsTable";
import DeviceAnalytics from "../components/Analytics/DeviceAnalytics";
import Filters from "../components/Analytics/Filters";
import { getAggregatedData } from "../services/api/analyticsApi";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setAnalyticsFilterOptions } from "../redux/analytics/actions";
import { getMinSecObject } from "../services/utils/analyticsUtils";
import { enrichDataWithSubcollections } from "../services/data/analyticsData";
import { generateReportWithSections } from "../services/api/analyticsApi";
import { setSelectedCatalogue, setSelectedCatalogues } from "../redux/Catalogues/actions";
import HeaderComponent from "../components/Components/Headers/HeaderComponent";
import { useMediaQuery } from "react-responsive";
import ListAltIcon from '@mui/icons-material/ListAlt';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../data/base";

const endDate = dayjs().endOf("day");
const startDate = dayjs().subtract(7, "day").startOf("day");

const defaultDateRange = [startDate, endDate];

const { Title, Text } = Typography;

const Analytics = () => {

    const dispatch = useDispatch();
    // Ref per accedere ai metodi del componente Filters
    const filtersRef = useRef(null);

    const [messageApi, contextHolder] = message.useMessage();
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 768px)" });

    const [loading, setLoading] = useState(false);
    const [loadingSelect, setLoadingSelect] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [reportData, setReportData] = useState({});
    const [filteredSessionList, setFilteredSessionList] = useState([]);
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [typeData, setTypeData] = useState("all");
    const [aggregateList, setAggregateList] = useState([]);
    const [filteredAggList, setFilteredAggList] = useState([]);
    const [overviewData, setOverviewData] = useState();
    const [selectedValue, setSelectedValue] = useState(null);

    const filterOptions = useSelector((state) => state.analytics.filterOptions);
    const { glass, variant } = filterOptions;
    const selectedCatalog = useSelector((state) => state.catalogues.selectedCatalog);
    const selectedCatalogs = useSelector((state) => state.catalogues.selectedCatalogs || []);
    const glassList = useSelector((state) => state.analytics.glassList);
    const catalogsList = useSelector((state) => state.catalogues.listaCatalogues);

    async function getDataAnalyticsOverview(data) {
        try {
            const {
                totalSession,
                totalUser,
                totalNewUser,
                totalAvgTime,
                totalAvgEndTime,
                totalAvgSpU,
                ..._data
            } = data;

            const startDate = dateRange[0].format("YYYY/MM/DD HH:mm:ss");
            const endDate = dateRange[1].format("YYYY/MM/DD HH:mm:ss");

            setReportData((prevReportData) => ({
                ...prevReportData,
                catalog: selectedCatalog.nameCatalog,
                typeData,
                startDate,
                endDate,
                totalSession,
                totalUser,
                totalNewUser,
                totalAvgEndTime,
                totalAvgSpU,
            }));

            setOverviewData(_data);
        } catch (error) {
            // gestione errore
        }
    }

    useEffect(() => {
        let selectedModel = "";
        if (glass) {
            selectedModel = glassList?.find((modello) => modello.id === glass);
        }
        setReportData({
            model: selectedModel?.nome_modello ?? "All",
        });
    }, [glass, filterOptions, glassList]);

    const getServiceType = (service) => {
        switch (service) {
            case "app":
                return "APP";
            case "3d":
                return "3D VIEWER";
            case "WebVto":
                return "WEB VTO";
            case "all":
                return "ALL"
            default:
                return "";
        }
    }

    useEffect(() => {
        // Filtra i dati basandosi sulle opzioni del filtro
        let filteredAggs =
            aggregateList?.filter((session) => {
                // Filtro per tipo di servizio
                if (Array.isArray(typeData)) {
                    // Se include 'all', non applicare filtro per servizio
                    if (typeData.includes('all')) {
                        // Non filtrare per servizio
                    } else {
                        // Verifica se il servizio della sessione è incluso tra quelli selezionati
                        const serviceMatches = typeData.some(service =>
                            session.service === getServiceType(service)
                        );
                        if (!serviceMatches) {
                            return false;
                        }
                    }
                } else {
                    // Retrocompatibilità con la vecchia implementazione
                    if (typeData !== "all" && session.service !== getServiceType(typeData)) {
                        return false;
                    }
                }

                let result = true;

                // Filtro per occhiali (products)
                const gData = session.list_products;
                if (
                    !!glass &&
                    gData &&
                    !gData.some((value) => {
                        // Aggiorna la sessione con i dati degli occhiali selezionati
                        if (value.productRef === glass) {
                            session.avgEngagementTime = getMinSecObject(value.averageEngagementTime);
                            session.avgSessionTime = getMinSecObject(value.averageSessionTime);
                            session.averageSessionsPerUser = value.averageSessionsPerUser;
                            session.newUsersCount = value.newUsersCount;
                            session.totalSessions = value.numberOfSessions;
                            session.totalUniqueUsers = value.numberOfUniqueUsers;
                        }
                        return value.productRef === glass;
                    })
                ) {
                    result = false;
                }

                // Filtro per varianti
                const vData = session.list_variants;
                if (
                    !!variant &&
                    vData &&
                    !vData.some((value) => {
                        // Aggiorna la sessione con i dati della variante selezionata
                        if (value.variantRef === variant) {
                            session.avgEngagementTime = getMinSecObject(value.averageEngagementTime);
                            session.avgSessionTime = getMinSecObject(value.averageSessionTime);
                            session.averageSessionsPerUser = value.averageSessionsPerUser;
                            session.newUsersCount = value.newUsersCount;
                            session.totalSessions = value.numberOfSessions;
                            session.totalUniqueUsers = value.numberOfUniqueUsers;
                        }
                        return value.variantRef === variant;
                    })
                ) {
                    result = false;
                }

                return result;
            }) ?? [];

        // Aggiorna le sessioni filtrate con i riferimenti selezionati
        filteredAggs =
            filteredAggs.map((session) => {
                const updatedSession = { ...session };

                if (!!glass) {
                    updatedSession.productRefs = [glass];
                }
                if (!!variant) {
                    updatedSession.variantRefs = [variant];
                }

                return updatedSession;
            }) ?? [];

        // Imposta la lista delle sessioni filtrate
        setFilteredAggList(filteredAggs);
    }, [glass, variant, typeData, aggregateList]);

    const aggregateCache = new Map();

    const fetchAggregate = async () => {
        try {
            setLoading(true);

            // Verifica se utilizzare "all", un singolo catalogo, o cataloghi multipli
            let allCatalogIds;

            // Se c'è solo un catalogo selezionato ed è "all", usa direttamente catalogsList
            if (selectedCatalogs.length === 1 && selectedCatalogs[0].id === 'all') {
                allCatalogIds = catalogsList || [];
            } else {
                // Altrimenti, usa gli ID dei cataloghi selezionati
                allCatalogIds = selectedCatalogs.map(catalog => catalog.id);
            }

            if (allCatalogIds.length === 0) {
                setAggregateList([]);
                setLoading(false);
                return;
            }

            // Crea una chiave di cache basata sui cataloghi selezionati e date
            // Utilizza una stringa concatenata degli ID ordinati per garantire consistenza
            const catalogIdsKey = [...allCatalogIds].sort().join('-');
            const cacheKey = `${catalogIdsKey}-${dateRange[0].format("YYYY-MM-DD")}-${dateRange[1].format("YYYY-MM-DD")}`;

            // Riabilito la cache
            if (aggregateCache.has(cacheKey)) {
                setAggregateList(aggregateCache.get(cacheKey));
                setLoading(false);
                return;
            }

            // Formatta le date
            const startDate = dateRange[0].format("YYYY/MM/DD");
            const endDate = dateRange[1].format("YYYY/MM/DD");

            try {
                // Chiamata al servizio per ottenere i dati aggregati
                const aggregateData = await getAggregatedData(allCatalogIds, startDate, endDate);
                // console.log("Dati aggregati trovati:", aggregateData);
                // console.log("Numero documenti aggregati:", aggregateData?.length || 0);

                if (!aggregateData || aggregateData.length === 0) {
                    // console.log("Nessun dato aggregato trovato");
                    setAggregateList([]);
                    setLoading(false);
                    return;
                }

                aggregateCache.set(cacheKey, aggregateData);
                setAggregateList(aggregateData);

                try {
                    const enrichedData = await enrichDataWithSubcollections(aggregateData);
                    aggregateCache.set(cacheKey, enrichedData);
                    setAggregateList(enrichedData);
                } catch (enrichError) {
                    console.error("Error enriching data:", enrichError);
                }
            } catch (error) {
                messageApi.error("Error retrieving aggregate data");
                setAggregateList([]);
            }

        } catch (error) {
            messageApi.error("Failed to fetch aggregate data. Please try again.");
            setAggregateList([]);
        } finally {
            setLoading(false);
        }
    };

    // Aggiorniamo le dipendenze per monitorare sia il catalogo singolo che quelli multipli
    useEffect(() => {
        fetchAggregate();
    }, [selectedCatalogs, dateRange, refresh]);

    const handleServiceChanged = (value) => {
        setTypeData(value);
    }

    const handleClearAllFilters = () => {
        // Reset tutti i filtri incluse le date
        dispatch(
            setAnalyticsFilterOptions({
                glass: "",
                variant: "",
            })
        );

        // Reset date range e tipo dati
        setDateRange(defaultDateRange);
        setTypeData(["all"]);
        setSelectedValue(null);

        // Reset sia il singolo catalogo che i cataloghi multipli
        const allCatalog = { id: "all", nameCatalog: "All Catalogues" };
        dispatch(setSelectedCatalogue(allCatalog));
        dispatch(setSelectedCatalogues([allCatalog]));

        // Forza il refresh dei dati
        setRefresh((r) => !r);
    };

    const onChangeDate = (dates) => {
        if (dates) {
            setDateRange([dates[0], dates[1]]);
        } else {
            setDateRange(defaultDateRange);
        }
    };

    const handleClearFilter = () => {
        // Reset filtri in Redux (escluse le date)
        dispatch(
            setAnalyticsFilterOptions({
                glass: "",
                variant: "",
            })
        );

        // Reset solo tipo dati, NON le date
        setTypeData(["all"]);
        setSelectedValue(null);

        // Reset sia il singolo catalogo che i cataloghi multipli
        const allCatalog = { id: "all", nameCatalog: "All Catalogues" };
        dispatch(setSelectedCatalogue(allCatalog));
        dispatch(setSelectedCatalogues([allCatalog]));

        // Forza il refresh dei dati
        setRefresh((r) => !r);
    };

    // REPORT
    const [showModal, setShowModal] = useState(false);
    const [catalogProfileData, setCatalogProfileData] = useState([]);
    const [allCatalogsSelected, setAllCatalogsSelected] = useState(false);
    const [reportSelectedCatalogs, setReportSelectedCatalogs] = useState([]);
    const [startDateReport, setStartDateReport] = useState('');
    const [endDateReport, setEndDateReport] = useState('');
    const [error, setError] = useState('');

    // Stato per le sezioni del report da includere
    const [reportSections, setReportSections] = useState({
        overview: false,
        deviceAnalytics: false,
        variantsDetails: false
    });

    const handleCloseModalProva = () => {
        setShowModal(false);
    };

    // Effetto per caricare i dati dei cataloghi quando il modale si apre
    useEffect(() => {
        if (showModal) {
            // Utilizziamo catalogsList che è già disponibile nello stato
            const loadCatalogData = async () => {
                try {
                    if (!catalogsList || catalogsList.length === 0) {
                        setCatalogProfileData([]);
                        return;
                    }

                    const promises = catalogsList.map(async (id) => {
                        try {
                            const docRef = await getDoc(doc(db, "Catalogues", id));
                            if (docRef.exists()) {
                                const catalogData = docRef.data();
                                // Filtra i cataloghi che sono Gateway catalogs
                                if (catalogData.isGatewayCatalog === true) {
                                    return null;
                                }
                                return {
                                    id: docRef.id,
                                    nome_brand: catalogData.nameCatalog || "Unnamed Catalog"
                                };
                            }
                            return null;
                        } catch (error) {
                            return null;
                        }
                    });

                    const results = await Promise.all(promises);
                    const validCatalogs = results
                        .filter(Boolean)
                        .sort((a, b) => (a.nome_brand || "").localeCompare(b.nome_brand || ""));
                    setCatalogProfileData(validCatalogs);

                    // Inizializza tutti i cataloghi come selezionati per default
                    const allCatalogIds = validCatalogs.map(cat => cat.id);
                    setReportSelectedCatalogs(allCatalogIds);
                    setAllCatalogsSelected(true);
                } catch (error) {
                    messageApi.error("Error loading catalogs data");
                    setCatalogProfileData([]);
                }
            };

            loadCatalogData();

            // Inizializza le date del report con le stesse date dell'UI
            // Usa lo stesso formato dell'UI per garantire coerenza
            setStartDateReport(dateRange[0].format("YYYY-MM-DD"));
            setEndDateReport(dateRange[1].format("YYYY-MM-DD"));

            // Inizializza le sezioni del report
            setReportSections({
                overview: true,
                variantsDetails: true,
                deviceAnalytics: true
            });
        }
    }, [showModal, catalogsList, dateRange]);

    const handleReportSectionChange = (e) => {
        const { name, checked } = e.target;

        if (name === 'all') {
            // Se "all" è selezionato, seleziona tutte le sezioni
            setReportSections({
                overview: checked,
                variantsDetails: checked,
                deviceAnalytics: checked
            });
        } else {
            // Altrimenti, aggiorna solo la sezione specifica
            setReportSections(prev => ({
                ...prev,
                [name]: checked
            }));
        }
    };

    const isAllSectionsSelected = () => {
        return Object.values(reportSections).every(value => value === true);
    };

    const isAnySectionSelected = () => {
        return Object.values(reportSections).some(value => value === true);
    };

    const handleSelectAllChange = (e) => {
        const isChecked = e.target.checked;
        setAllCatalogsSelected(isChecked);
        if (isChecked) {
            const allCatalogs = catalogProfileData.map(catalog => catalog.id);
            setReportSelectedCatalogs(allCatalogs);
        } else {
            setReportSelectedCatalogs([]);
        }
    };

    const handleCatalogChange = (checkedValues) => {
        setReportSelectedCatalogs(checkedValues);
    };

    const handleStartDateChange = (date) => {
        if (!date) {
            setStartDateReport(null);
            setError('');
            return;
        }

        const value = date.format("YYYY-MM-DD");

        if (endDateReport && dayjs(value).isAfter(dayjs(endDateReport))) {
            setError('Start date cannot be later than end date.');
        } else {
            setStartDateReport(value);
            setError('');
        }
    };

    const handleEndDateChange = (date) => {
        if (!date) {
            setEndDateReport(null);
            setError('');
            return;
        }

        const value = date.format("YYYY-MM-DD");

        if (startDateReport && dayjs(value).isBefore(dayjs(startDateReport))) {
            setError('End date cannot be earlier than start date.');
        } else {
            setEndDateReport(value);
            setError('');
        }
    };

    const isFormValid = () => {
        return startDateReport && endDateReport && !error && isAnySectionSelected();
    };

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const startDate = startDateReport;
            const endDate = endDateReport;
            const selectedCatalogues = reportSelectedCatalogs;

            // Otteniamo i dati grezzi - usa lo stesso formato dell'UI con startOf/endOf
            // per garantire coerenza nella query di Firebase
            const formattedStartDate = dayjs(startDate).startOf("day").format("YYYY/MM/DD");
            const formattedEndDate = dayjs(endDate).endOf("day").format("YYYY/MM/DD");

            // Ottieni cataloghi - usa la stessa logica dell'UI
            let cataloguesToUse;
            if (selectedCatalogues.length === 0 ||
                (selectedCatalogues.length === catalogProfileData.length && allCatalogsSelected)) {
                // Se non ci sono cataloghi selezionati o sono tutti selezionati, usa catalogsList (come nell'UI)
                cataloguesToUse = catalogsList || [];
            } else {
                // Altrimenti, usa gli ID dei cataloghi selezionati
                cataloguesToUse = selectedCatalogues;
            }

            const aggregateData = await getAggregatedData(cataloguesToUse, formattedStartDate, formattedEndDate);
            // Arricchisci i dati
            let enrichedData = aggregateData;
            try {
                enrichedData = await enrichDataWithSubcollections(aggregateData);
            } catch (enrichError) {
                console.error("Error enriching data:", enrichError);
            }

            // Non filtriamo più per servizio - includiamo tutti i dati
            const reportFilteredData = enrichedData;

            // Passa i dati alla funzione di generazione del report con le sezioni selezionate
            const success = await generateReportWithSections(
                startDate,
                endDate,
                selectedCatalogues,
                reportFilteredData,
                reportSections // Aggiungiamo le sezioni selezionate
            );

            if (success) {
                messageApi.success("Report successfully generated!");
            }
        } catch (error) {
            console.error("Error during report download:", error);
            messageApi.error("Error generating the report.");
        } finally {
            setDownloadLoading(false);
            setShowModal(false);  // Chiudi il modale dopo il download
        }
    };

    return (
        <Layout>
            <Layout.Content id="analytics">
                {contextHolder}

                <HeaderComponent
                    title="VTOs - Analytics"
                    buttonText="Create your report"
                    buttonIcon={<ListAltIcon />}
                    onButtonClick={() => setShowModal(true)}
                    isVisible={!isTabletOrMobile}
                    useSpaceBetween={false}
                />

                <Filters
                    ref={filtersRef}
                    typeData={typeData}
                    setTypeData={setTypeData}
                    onChangeService={handleServiceChanged}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                    setLoadingSelect={setLoadingSelect}
                    loadingSelect={loadingSelect}
                    dateRange={dateRange}
                    onChangeDate={onChangeDate}
                    onClearFilter={handleClearFilter}
                    downloadLoading={downloadLoading}
                />

                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={3} style={{ marginBottom: '1rem' }}>
                            Overview
                        </Title>
                        <AnalyticsOverviewCard
                            loading={loading}
                            aggregateList={filteredAggList}
                            getData={getDataAnalyticsOverview}
                        />
                    </Col>
                    <Col span={24}>
                        <Title level={3} style={{ marginBottom: '1rem' }}>
                            Catalogue Time Focus
                        </Title>
                        <AnalyticsTable
                            loading={loading}
                            aggregateList={filteredAggList}
                        />
                    </Col>
                    <Col span={24}>
                        <Title level={3} style={{ marginBottom: '1rem' }}>
                            Devices Analytics
                        </Title>
                        <DeviceAnalytics
                            loading={loading}
                            sessionList={filteredSessionList}
                            aggregateList={filteredAggList}
                            onResetFilters={handleClearFilter}
                        />
                    </Col>
                </Row>

                <Modal
                    title={<Title level={4} style={{ margin: 0 }}>Report Download</Title>}
                    open={showModal}
                    onCancel={handleCloseModalProva}
                    footer={null}
                    width={1000}
                    centered
                >
                    <Row gutter={[24, 24]}>
                        <Col span={24}>
                            <Title level={5} style={{ marginBottom: 12 }}>
                                Select Date Range
                            </Title>
                            <Row gutter={[24, 0]} justify="start">
                                <Col md={8} sm={12}>
                                    <Form layout="vertical" style={{ marginBottom: 0 }}>
                                        <Form.Item
                                            style={{ marginBottom: 0 }}
                                        >
                                            <DatePicker
                                                format="DD/MM/YYYY"
                                                value={startDateReport ? dayjs(startDateReport, "YYYY-MM-DD") : null}
                                                onChange={(date) => handleStartDateChange(date)}
                                                style={{ width: "100%" }}
                                                placeholder="Select Start Date"
                                                size="middle"
                                            />
                                        </Form.Item>
                                    </Form>
                                </Col>
                                <Col md={8} sm={12}>
                                    <Form layout="vertical" style={{ marginBottom: 0 }}>
                                        <Form.Item
                                            style={{ marginBottom: 0 }}
                                        >
                                            <DatePicker
                                                format="DD/MM/YYYY"
                                                value={endDateReport ? dayjs(endDateReport, "YYYY-MM-DD") : null}
                                                onChange={(date) => handleEndDateChange(date)}
                                                style={{ width: "100%" }}
                                                size="middle"
                                                placeholder="Select End Date"
                                            />
                                        </Form.Item>
                                    </Form>
                                </Col>
                            </Row>
                            {error && <Text type="danger" style={{ display: 'block', marginTop: 8 }}>{error}</Text>}
                        </Col>
                    </Row>

                    <Row gutter={[32, 24]} style={{ marginTop: 16 }}>
                        <Col md={12}>
                            <Title level={5} style={{ marginBottom: 16 }}>
                                Select Catalog(ues)
                            </Title>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0 4px' }}>
                                <Checkbox
                                    checked={allCatalogsSelected}
                                    onChange={handleSelectAllChange}
                                    style={{ marginBottom: 8, marginRight: 8 }}
                                >
                                    <Text strong>SELECT ALL</Text>
                                </Checkbox>
                                <Divider style={{ margin: '12px 0' }} />
                                <Checkbox.Group
                                    value={reportSelectedCatalogs}
                                    onChange={handleCatalogChange}
                                    style={{ display: 'flex', flexWrap: 'wrap' }}
                                >
                                    {catalogProfileData
                                        .sort((a, b) => a.nome_brand.localeCompare(b.nome_brand))
                                        .map((catalog) => (
                                            <Checkbox
                                                key={catalog.id}
                                                value={catalog.id}
                                                disabled={allCatalogsSelected}
                                                style={{ marginBottom: 8, marginRight: 12 }}
                                            >
                                                <Text>{catalog.nome_brand.toUpperCase()}</Text>
                                            </Checkbox>
                                        ))}
                                </Checkbox.Group>
                            </div>
                        </Col>

                        <Col md={12}>
                            <Title level={5} style={{ marginBottom: 16 }}>
                                Select Report Section(s)
                            </Title>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '0 4px' }}>
                                <Checkbox
                                    name="all"
                                    checked={isAllSectionsSelected()}
                                    onChange={handleReportSectionChange}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Text strong>All Sections</Text>
                                </Checkbox>
                                <Divider style={{ margin: '12px 0' }} />
                                <Checkbox
                                    name="overview"
                                    checked={reportSections.overview}
                                    onChange={handleReportSectionChange}
                                    style={{ marginBottom: 12 }}
                                >
                                    Overview
                                </Checkbox>
                                <Checkbox
                                    name="deviceAnalytics"
                                    checked={reportSections.deviceAnalytics}
                                    onChange={handleReportSectionChange}
                                    style={{ marginBottom: 12 }}
                                >
                                    Device Analytics
                                </Checkbox>
                                <Checkbox
                                    name="variantsDetails"
                                    checked={reportSections.variantsDetails}
                                    onChange={handleReportSectionChange}
                                >
                                    Variants Details
                                </Checkbox>
                            </div>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '24px 0' }} />

                    <Row justify="center" style={{ marginTop: 8 }}>
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            disabled={!isFormValid() || downloadLoading}
                            icon={
                                downloadLoading ? (
                                    <Spin size="small" />
                                ) : (
                                    <DownloadOutlined />
                                )
                            }
                            size="large"
                            style={{ minWidth: 200, height: 40 }}
                        >
                            {downloadLoading ? "Downloading..." : "Proceed to Download"}
                        </Button>
                    </Row>
                </Modal>
            </Layout.Content>
        </Layout>
    );
};

export default Analytics;